import { DateText } from "@/components/DateText";
import { QuoteProvenance } from "@/components/submissions/QuoteProvenance";
import { useId, type ReactNode } from "react";
import { MissingFieldsList } from "@/components/submissions/MissingFieldsList";
import { CarrierDeclined } from "@/components/submissions/CarrierDeclined";
import { CarrierStepProblem } from "@/components/submissions/CarrierStepProblem";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import { CoverageTermsSummary } from "@/components/CoverageTermsSummary";
import {
  coveragePartsOf,
  policyTypeLabel,
  termsTotalsOf,
} from "@/lib/submissions/coverageRows";
import { Fact } from "@/components/submissions/Fact";
import { useAuth } from "@/auth/AuthProvider";
import { AskSignalToCancel } from "@/components/submissions/QuoteBinding";
import { QuoteSubjectivities } from "@/components/submissions/QuoteSubjectivities";
import { QuoteFigure } from "@/components/submissions/QuoteCard";
import { buttonClass } from "@/components/buttonStyles";
import { formatIsoDate, formatLimit, formatUsd } from "@/lib/displayFormat";
import {
  chargeGroups,
  chargeRate,
  wcClassLines,
  type CoverageTerms,
  type CoverageType,
  type PremiumCharge,
  type WcClassLine,
} from "@/lib/submissions/coverageTerms";
import { referralStanding } from "@/lib/submissions/quoteConditions";
import {
  canRequestManualQuote,
  manualQuoteLine,
} from "@/lib/submissions/manualQuotes";
import {
  canAskForReview,
  REVIEW_ACTION_LABEL,
  REVIEW_LABELS,
  reviewLine,
  reviewNextLine,
} from "@/lib/submissions/quoteReview";
import { hasCapability } from "@/lib/brokerAuth";
import { bindStanding, canBind } from "@/lib/submissions/quoteBinding";
import { BoundSummary } from "@/components/submissions/BoundSummary";
import type {
  MissingField,
  OfferTermsGroupView,
} from "@/lib/submissions/brokerSubmissions";
import {
  REFERRAL_NEXT,
  REFERRED_LABEL,
  appetiteLine,
  outcomeNotice,
  referralLine,
} from "@/lib/submissions/outcomes";
import {
  employersLiability,
  expiredLine,
  firmResultLine,
  formatTermValue,
  indicationOnlyLine,
  onItsWay,
  outdatedLine,
  planSentence,
  problemLine,
  showsPrice,
  type QuoteCard,
} from "@/lib/submissions/quoteCards";

// BRK-S347 — a carrier's offer in detail, in the quote popup, from what PMS
// sends a broker. Right after Price, its rating by class code and how its
// premium is built (BRK-S346), from PMS-S553's terms as PMS-S745 fills them,
// each only when PMS sent it: nothing is summed or guessed here.

function Section({ title, children }: { title: string; children: ReactNode }) {
  const id = useId();
  return (
    <section aria-labelledby={id} className="flex flex-col gap-2">
      <h3 id={id} className="text-subheading text-brk-text">
        {title}
      </h3>
      {children}
    </section>
  );
}

/** A label and its value on one line, the value right-aligned. */
function Line({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex flex-wrap items-baseline justify-between gap-x-3">
      <dt className="text-brk-dim">{label}</dt>
      <dd className="text-right tabular-nums text-brk-text">{value}</dd>
    </div>
  );
}

/** Where the offer stands, beyond its chip: this visit's quote, an
 *  outdated price, a referral, or a carrier with no price. Null when the chip
 *  says it all. */
function statusLine(card: QuoteCard, canAsk: boolean): string | null {
  const { run, quote, column, carrier } = card;
  if (run?.kind === "pricing")
    return `${carrier} is pricing — this can take up to a minute.`;
  // Another caller's request, or one PMS keeps trying (BRK-S460): Price says
  // so in the card's words, and no earlier quote's standing reads beside it.
  if (run?.kind === "waiting") return null;
  if (run?.kind === "failed") return problemLine(run.problem, carrier);
  if (run?.kind === "done") return firmResultLine(carrier, run.result);
  // Chosen, or asked to bind (BRK-S209): where that stands, from PMS.
  const bind = bindStanding(quote);
  if (bind) return bind.line;
  if (quote?.outdated) return outdatedLine(quote, carrier);
  if (quote && card.expired) return expiredLine(quote, carrier);
  // Signal's request to the carrier's underwriter (PMS-S749, BRK-S362): once
  // answered, the answer; while asked, that it's asked, never "contact
  // Signal".
  const standing = referralStanding(quote?.referral);
  // An open review, until the underwriter answers (BRK-S463), by where it
  // stands: Signal may have opened it itself (PMS-S870).
  if (quote?.reviewRequest && REVIEW_LABELS.includes(card.chip.label))
    return `${reviewLine(quote.reviewRequest.status, carrier)} This quote isn’t bindable until it’s approved.`;
  if (standing?.chip) return standing.line;
  // A saved referral says whose underwriter, as this visit's would
  // (BRK-S354), and what's next: the broker asks from the header when they
  // can (BRK-S463, BRK-S465), else Signal.
  if (card.chip.label === REFERRED_LABEL)
    return `${referralLine(carrier, quote?.totalPremium ?? null)} ${standing?.line ?? (canAsk ? reviewNextLine(carrier) : REFERRAL_NEXT)}`;
  // An indication from a carrier that prices only when it quotes is its
  // underwriter's to review: asking again only re-prices it (BRK-S364).
  if (quote?.pricesOnlyAtQuote && quote.kind === "indication")
    return indicationOnlyLine(carrier);
  if (quote) return null;
  // A carrier Signal quotes by hand, until its quote is in (BRK-S395).
  if (column.manual) return manualQuoteLine(carrier, column.manual.request);
  return outcomeNotice(column.outcome ?? "error", column.appetite, carrier);
}

export function QuoteDetail({
  card,
  termGroups,
  coverageTypes,
  missingFieldHref,
  conditions,
}: {
  card: QuoteCard;
  termGroups: OfferTermsGroupView[];
  coverageTypes?: CoverageType[];
  missingFieldHref?: (field: MissingField) => string | null;
  /** Where the quote's subjectivities are answered (BRK-S362): its
   *  submission, the insured's account for downloads, and how the quotes are
   *  read again. Without it, they don't show. Its notes are the popup's
   *  Notes tab (BRK-S465). */
  conditions?: {
    submissionId: string;
    accountId: string | null;
    onChanged: () => void | Promise<void>;
  };
}) {
  const q = card.quote;
  const parts = coveragePartsOf(q?.terms);
  const { identity } = useAuth();
  // Where the broker can ask for the underwriter's review, the Status points
  // at the header's button (BRK-S463, BRK-S465).
  const canAsk = !!conditions && canAskForReview(card, identity);
  const status = statusLine(card, canAsk);
  const missing = card.column.missingFields;
  const appetite = card.column.appetite
    ? appetiteLine(card.column.appetite)
    : "";
  if (card.chip.label === "Declined" && !q)
    return <CarrierDeclined carrier={card.carrier} reasons={card.reasons} />;
  // Bound (BRK-S468): what was bound, at the top, in place of the Status and
  // with its open conditions.
  const bound = q?.stage === "bound";
  return (
    <div className="flex flex-col gap-6 text-sm">
      {bound && q && <BoundSummary quote={q} conditions={conditions} />}
      <Section title="Price">
        {showsPrice(card) ? (
          <dl className="flex flex-col gap-2">
            <div className="flex flex-wrap items-baseline justify-between gap-x-3">
              <dt className="text-brk-dim">Total cost</dt>
              <dd className="text-figure tabular-nums text-brk-text">
                <QuoteFigure
                  quote={q}
                  text={formatUsd(
                    card.quote.terms?.totals?.totalCost ?? card.quote.totalCost,
                  )}
                />
              </dd>
            </div>
            <Line
              label="Premium"
              value={
                <QuoteFigure
                  quote={q}
                  text={formatUsd(
                    card.quote.terms?.totals?.totalPremium ??
                      card.quote.totalPremium,
                  )}
                />
              }
            />
            {termsTotalsOf(card.quote.terms).length === 0 && (
              <Line
                label="Taxes & fees"
                value={
                  <QuoteFigure
                    quote={q}
                    text={formatUsd(card.quote.taxesAndFees)}
                  />
                }
              />
            )}
          </dl>
        ) : (
          <div className="flex flex-col gap-1">
            <p className="text-brk-text">{card.summary ?? "No price yet."}</p>
            {appetite && <p className="text-brk-dim">{appetite}</p>}
          </div>
        )}
      </Section>

      {showsPrice(card) && wcClassLines(card.quote.terms).length > 0 && (
        <Section title="Rating by class code">
          <ClassRating lines={wcClassLines(card.quote.terms)} />
        </Section>
      )}

      {showsPrice(card) &&
        card.quote.terms &&
        card.quote.terms.charges.length > 0 && (
          <Section title="How the premium is built">
            <PremiumBuilt terms={card.quote.terms} />
          </Section>
        )}

      {showsPrice(card) && parts.length > 0 && (
        <Section title="Coverage terms">
          <p className="text-sm text-brk-dim">
            {policyTypeLabel(card.quote.terms)}
          </p>
          <CoverageTermsSummary
            terms={card.quote.terms}
            coverageTypes={coverageTypes}
            marketType={card.carrierProfile?.market?.type}
            billing={card.quote.billing}
          />
        </Section>
      )}

      {showsPrice(card) && card.quote.paymentPlans.length > 0 && (
        <Section title="Payment options">
          <dl className="flex flex-col gap-2">
            {card.quote.paymentPlans.map((plan, i) => {
              const { label, text } = planSentence(plan);
              return <Line key={i} label={label} value={text} />;
            })}
          </dl>
        </Section>
      )}

      {parts.length === 0 &&
        showsPrice(card) &&
        employersLiability(card.quote).length > 0 && (
          <Section title="Coverage">
            <p className="text-brk-dim">Employers liability</p>
            <dl className="flex flex-col gap-2">
              {employersLiability(card.quote).map((limit) => (
                <Line
                  key={limit.label}
                  label={limit.label}
                  value={limit.value}
                />
              ))}
            </dl>
          </Section>
        )}

      {q && (
        <Section title="Policy">
          <QuoteProvenance quote={q} statusLabel={card.chip.label} />
          {q.kind === "indication" && (
            <p className="text-brk-dim">Indication — get a quote to bind</p>
          )}

          <dl className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Fact
              label="Policy period"
              value={
                q.policyEffectiveDate ? (
                  <>
                    {!q.policyExpirationDate && "From "}
                    <DateText value={q.policyEffectiveDate} />
                    {q.policyExpirationDate && (
                      <>
                        {" "}
                        – <DateText value={q.policyExpirationDate} />
                      </>
                    )}
                  </>
                ) : undefined
              }
            />
            {q.quoteNumber && (
              <Fact
                label="Portal quote number"
                value={q.quoteNumber}
                identifier
              />
            )}
            <Fact
              label="Quote number"
              value={q.carrierQuoteNumber ?? undefined}
              identifier
            />
            <Fact
              label="Type"
              value={
                q.kind === "quote"
                  ? "Quote — priced on the carrier’s own questions"
                  : "Indication — an estimate to compare"
              }
            />
            {q.quoteValidUntil && (
              <Fact
                label="Valid until"
                value={<DateText value={q.quoteValidUntil} />}
              />
            )}
            <Fact label="Priced on" value={formatIsoDate(q.createdAt)} />
          </dl>
        </Section>
      )}

      {!bound && (status || card.reasons.length > 0 || missing.length > 0) && (
        <Section title="Status">
          {card.run?.kind === "failed" &&
          card.run.problem.kind === "rejected" ? (
            <CarrierStepProblem
              problem={card.run.problem}
              carrier={card.carrier}
            />
          ) : (
            status && (
              <p
                className={
                  (q?.outdated || card.expired) && !card.run
                    ? "text-brk-danger"
                    : "text-brk-text"
                }
              >
                {status}
              </p>
            )
          )}
          {conditions && q && (
            <AskSignalToCancel
              submissionId={conditions.submissionId}
              quote={q}
              onChanged={conditions.onChanged}
            />
          )}
          {card.reasons.length > 0 && card.chip.label === REFERRED_LABEL && (
            <h4 className="text-subheading text-brk-text">
              Why it was referred
            </h4>
          )}
          {card.reasons.length > 0 && (
            <ul className="list-inside list-disc text-brk-dim">
              {card.reasons.map((reason) => (
                <li key={reason}>{reason}</li>
              ))}
            </ul>
          )}
          {missing.length > 0 && (
            <MissingFieldsList
              missing={missing}
              missingFieldHref={missingFieldHref}
              className="list-inside list-disc text-brk-dim"
            />
          )}
        </Section>
      )}

      {!bound &&
        conditions &&
        q?.subjectivities &&
        q.subjectivities.length > 0 && (
          <Section title="Subjectivities">
            <QuoteSubjectivities
              submissionId={conditions.submissionId}
              quoteId={q.id}
              accountId={conditions.accountId}
              subjectivities={q.subjectivities}
              onChanged={conditions.onChanged}
            />
          </Section>
        )}

      {termGroups.length > 0 && (
        <Section
          title={parts.length > 0 ? "Requested terms" : "What you asked for"}
        >
          <p className="text-brk-dim">
            The insured’s requested terms, the same on every carrier’s quote.
          </p>
          {termGroups.map((group) => (
            <div key={group.label} className="flex flex-col gap-2">
              <h4 className="text-subheading text-brk-dim">{group.label}</h4>
              <dl className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {group.terms.map((term) => (
                  <Fact
                    key={term.key}
                    label={term.label}
                    value={formatTermValue(term)}
                  />
                ))}
              </dl>
            </div>
          ))}
        </Section>
      )}
    </div>
  );
}

/**
 * The popup's one action for an offer, by what it needs most:
 * 0. a quote on its way has nothing to ask for until it's in (BRK-S460):
 *    asking again only meets PMS's 409 for the request it already holds;
 * 1. an outdated price is priced again: by the markets' check, or, for a
 *    carrier that prices only when it quotes (PMS-S750), by its own quote step,
 *    as the markets' check gives its appetite alone (BRK-S364); an expired
 *    one by its own quote step too, which PMS re-prices it with (BRK-S462);
 * 2. a quote PMS says can be bound asks Signal to bind it (BRK-S209); one
 *    chosen or asked to bind has nothing more here (its Status says where
 *    it stands); a referred one the broker can ask about asks for its
 *    underwriter's review, the same dialog as the header's menu (BRK-S468);
 * 3. an insured missing quote-floor details goes to the profile (BRK-S057);
 * 4. a current price from a carrier that prices only when it quotes has
 *    nothing to ask again: its underwriter's, as the Status says;
 * 5. a price with no carrier address this visit checks the markets again;
 * 6. otherwise a quote starts, continues, or is tried again.
 * A carrier Signal quotes by hand asks Signal for its quote, once (BRK-S395).
 * Null when there's nothing to do here.
 */
export function DetailAction({
  card,
  floorMissing,
  pulling,
  onUpdate,
  onCompleteProfile,
  onFirmUp,
  onBind,
  onRequestQuote,
  onRequestReview,
  asks = true,
}: {
  card: QuoteCard;
  floorMissing: MissingField[];
  pulling: boolean;
  onUpdate: () => void;
  onCompleteProfile?: () => void;
  onFirmUp: () => void;
  /** Open the bind view; without it, no bind is offered. */
  onBind?: () => void;
  /** Whether the popup asks the carrier's questions (BRK-S393). */
  asks?: boolean;
  /** Open the manual carrier's quote request; without it, none is offered. */
  onRequestQuote?: () => void;
  /** Open the underwriter review's dialog; without it, none is offered. */
  onRequestReview?: () => void;
}) {
  const { identity } = useAuth();
  const q = card.quote;
  if (onItsWay(card.run)) return null;
  if (card.column.manual && !q)
    return onRequestQuote &&
      canRequestManualQuote(card.column.manual.request, identity) ? (
      <button type="button" onClick={onRequestQuote} className={buttonClass()}>
        Request a quote
      </button>
    ) : null;
  if (!q && card.chip.label === "Declined" && card.column.carrierId)
    return (
      <button type="button" onClick={onFirmUp} className={buttonClass()}>
        Change answers
      </button>
    );
  // Its carrier prices it again: its own quote step, once it has an address.
  const priceAgain = () => {
    if (!card.column.carrierId)
      return (
        <button
          type="button"
          onClick={onUpdate}
          disabled={pulling}
          className={buttonClass({ variant: "secondary" })}
        >
          Check the markets again to get an updated quote
        </button>
      );
    if (floorMissing.length > 0)
      return (
        <FloorGate
          floorMissing={floorMissing}
          onCompleteProfile={onCompleteProfile}
        />
      );
    return (
      <button type="button" onClick={onFirmUp} className={buttonClass()}>
        {card.run?.kind === "answering"
          ? "Continue the updated quote"
          : card.run?.kind === "failed"
            ? "Try again"
            : "Get an updated quote"}
      </button>
    );
  };
  if (q?.outdated && q.pricesOnlyAtQuote) return priceAgain();
  if (q?.outdated)
    return (
      <button
        type="button"
        onClick={onUpdate}
        disabled={pulling}
        className={buttonClass()}
      >
        Get an updated quote
      </button>
    );
  if (card.expired) return priceAgain();
  if (q?.stage === "binding" || q?.stage === "bound") return null;
  if (onRequestReview && canAskForReview(card, identity))
    return (
      <button type="button" onClick={onRequestReview} className={buttonClass()}>
        {REVIEW_ACTION_LABEL}
      </button>
    );
  if (q && onBind && canBind(q, identity))
    return (
      <button type="button" onClick={onBind} className={buttonClass()}>
        Request bind
      </button>
    );
  if (q?.kind === "quote" && q.bindStatus === "quotable")
    return (q.bindAction ?? "none") !== "none" &&
      !hasCapability(identity, "quote:bind") ? (
      <p className="text-sm text-brk-dim sm:max-w-xs sm:text-right">
        Your role can’t request a bind. Ask your brokerage’s owner.
      </p>
    ) : null;
  if (card.canFirmUp && floorMissing.length > 0)
    return (
      <FloorGate
        floorMissing={floorMissing}
        onCompleteProfile={onCompleteProfile}
      />
    );
  if (q?.pricesOnlyAtQuote) return null;
  if (q?.kind === "indication" && !card.column.carrierId)
    return (
      <button
        type="button"
        onClick={onUpdate}
        disabled={pulling}
        className={buttonClass({ variant: "secondary" })}
      >
        Check the markets again to get a quote
      </button>
    );
  if (!card.canFirmUp) return null;
  return (
    <div className="flex flex-col gap-1 sm:items-end">
      {/* A carrier that prices only when it quotes opens an application with
          the insured's details to ask anything (Great American: saved on its
          side, with no delete), so it is said before the broker starts. The
          WC wizard's last step sent it already (BRK-S393). */}
      {asks && card.column.outcome === "appetite_only" && !card.run && (
        <p className="text-sm text-brk-dim">
          To get its questions, {card.carrier} will receive this insured’s
          details.
        </p>
      )}
      <button type="button" onClick={onFirmUp} className={buttonClass()}>
        {card.run?.kind === "answering"
          ? "Continue the quote"
          : card.run?.kind === "failed"
            ? "Try again"
            : "Get a quote"}
      </button>
    </div>
  );
}

/** The insured's profile, before a carrier's quote step can run (BRK-S057). */
function FloorGate({
  floorMissing,
  onCompleteProfile,
}: {
  floorMissing: MissingField[];
  onCompleteProfile?: () => void;
}) {
  return (
    <div className="flex flex-col gap-1 sm:items-end">
      <button
        type="button"
        onClick={onCompleteProfile}
        className={buttonClass()}
      >
        Complete the insured profile to quote
      </button>
      <span className="text-xs text-brk-dim">
        A quote needs: {floorMissing.map((f) => f.label).join(", ")}.
      </span>
    </div>
  );
}

/** A class line's name: its code, then its description when sent. */
const className = (line: WcClassLine) =>
  line.classDescription
    ? `${line.classCode} · ${line.classDescription}`
    : line.classCode;

/** Each class code's rating as the carrier sent it: a table from md, a
 *  stacked row per code on a phone. Payroll and premium read as money
 *  (D-10 #3); the rate as the carrier wrote it. */
function ClassRating({ lines }: { lines: WcClassLine[] }) {
  const theme = useTheme();
  const wide = useMediaQuery(theme.breakpoints.up("md"), { noSsr: true });
  const rate = (line: WcClassLine) => line.rate ?? "—";
  if (wide) {
    return (
      <table className="w-full text-left tabular-nums">
        <thead>
          <tr className="border-b border-brk-border text-brk-dim">
            <th scope="col" className="py-1.5 pr-3 font-medium">
              Class code
            </th>
            <th scope="col" className="py-1.5 pr-3 font-medium">
              State
            </th>
            <th scope="col" className="py-1.5 pr-3 text-right font-medium">
              Payroll
            </th>
            <th scope="col" className="py-1.5 pr-3 text-right font-medium">
              Rate
            </th>
            <th scope="col" className="py-1.5 text-right font-medium">
              Premium
            </th>
          </tr>
        </thead>
        <tbody>
          {lines.map((line, i) => (
            <tr
              key={`${line.state}-${line.classCode}-${String(i)}`}
              className="border-b border-brk-border last:border-0"
            >
              <td className="py-1.5 pr-3 text-brk-text">{className(line)}</td>
              <td className="py-1.5 pr-3 text-brk-text">{line.state}</td>
              <td className="py-1.5 pr-3 text-right text-brk-text">
                {formatLimit(line.payroll)}
              </td>
              <td className="py-1.5 pr-3 text-right text-brk-text">
                {rate(line)}
              </td>
              <td className="py-1.5 text-right text-brk-text">
                {formatUsd(line.premium)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }
  return (
    <ul className="flex flex-col gap-2">
      {lines.map((line, i) => (
        <li
          key={`${line.state}-${line.classCode}-${String(i)}`}
          className="rounded-lg border border-brk-border p-3"
        >
          <p className="break-words font-medium text-brk-text">
            {className(line)}
          </p>
          <dl className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1.5 tabular-nums">
            {[
              ["State", line.state],
              ["Payroll", formatLimit(line.payroll)],
              ["Rate", rate(line)],
              ["Premium", formatUsd(line.premium)],
            ].map(([label, value]) => (
              <div key={label} className="flex flex-col">
                <dt className="text-brk-dim">{label}</dt>
                <dd className="text-brk-text">{value}</dd>
              </div>
            ))}
          </dl>
        </li>
      ))}
    </ul>
  );
}

/** How the premium is built: its lines in PMS's order, each with its rate as
 *  applied, down to PMS's own totals. */
function PremiumBuilt({ terms }: { terms: CoverageTerms }) {
  const { premium, taxesAndFees } = chargeGroups(terms);
  return (
    <dl className="flex flex-col gap-1.5">
      {premium.map((charge, i) => (
        <ChargeRow key={`${charge.code}-${String(i)}`} charge={charge} />
      ))}
      <TotalRow label="Total premium" value={terms.totals.totalPremium} />
      {taxesAndFees.map((charge, i) => (
        <ChargeRow key={`${charge.code}-${String(i)}`} charge={charge} />
      ))}
      <TotalRow
        label="Total taxes and fees"
        value={terms.totals.taxesAndFees}
      />
      <TotalRow label="Total cost" value={terms.totals.totalCost} strong />
    </dl>
  );
}

function ChargeRow({ charge }: { charge: PremiumCharge }) {
  const rate = chargeRate(charge);
  return (
    <div
      data-charge
      className="flex flex-wrap items-baseline justify-between gap-x-3"
    >
      <dt className="flex min-w-0 flex-col text-brk-text">
        <span className="break-words">{charge.label}</span>
        {rate && <span className="text-caption text-brk-dim">{rate}</span>}
      </dt>
      <dd className="tabular-nums text-brk-text">{formatUsd(charge.amount)}</dd>
    </div>
  );
}

function TotalRow({
  label,
  value,
  strong = false,
}: {
  label: string;
  value: string;
  strong?: boolean;
}) {
  return (
    <div
      data-charge
      className={`flex flex-wrap items-baseline justify-between gap-x-3 border-t border-brk-border pt-1.5 ${strong ? "font-semibold" : "font-medium"}`}
    >
      <dt className="text-brk-text">{label}</dt>
      <dd className="tabular-nums text-brk-text">{formatUsd(value)}</dd>
    </div>
  );
}
